import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Checkbox } from '@/components/ui/checkbox';
import { useBulkSelection } from '@/hooks/useBulkSelection';
import { BulkActionToolbar } from '@/components/BulkActionToolbar';
import { Textarea } from '@/components/ui/textarea';
import { Plus, Trash2, Search, Building2, MapPin, Phone, Mail, HardHat, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// P.IVA lookup source labels (avoids nested ternaries in handlers)
const PIVA_SOURCE_LABELS = { openapi: 'Registro Imprese', VIES: 'VIES' };
const getPivaSourceLabel = (source) => PIVA_SOURCE_LABELS[source] || 'archivio';

export default function ClientiPage() {
  const [clienti, setClienti] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [cantiereDialogOpen, setCantiereDialogOpen] = useState(false);
  const [selectedCliente, setSelectedCliente] = useState(null);
  const [search, setSearch] = useState('');
  const [lookupLoading, setLookupLoading] = useState(false);
  const [pivaInput, setPivaInput] = useState('');
  const [form, setForm] = useState({
    ragione_sociale: '', partita_iva: '', codice_fiscale: '', indirizzo: '',
    citta: '', cap: '', provincia: '', email: '', pec: '', telefono: '', codice_sdi: '', note: ''
  });
  const [cantiereForm, setCantiereForm] = useState({ name: '', address: '', description: '' });

  useEffect(() => { fetchClienti(); }, []);

  const fetchClienti = async () => {
    try {
      const res = await axios.get(`${API}/clienti`, { withCredentials: true });
      setClienti(res.data);
    } catch { toast.error('Errore'); }
    finally { setLoading(false); }
  };

  const handleLookupPiva = async () => {
    const cleaned = pivaInput.replace(/[^0-9]/g, '');
    if (!cleaned || cleaned.length < 11) {
      toast.error('Inserisci una P.IVA valida (11 cifre)');
      return;
    }
    setLookupLoading(true);
    try {
      const res = await axios.get(`${API}/lookup-piva/${cleaned}`, { withCredentials: true });
      if (res.data.valid && res.data.ragione_sociale) {
        setForm({
          ...form,
          ragione_sociale: res.data.ragione_sociale || '',
          partita_iva: res.data.partita_iva_completa || `IT${res.data.partita_iva}`,
          indirizzo: res.data.indirizzo || '',
          citta: res.data.citta || '',
          cap: res.data.cap || '',
          provincia: res.data.provincia || '',
          codice_sdi: res.data.sdi_code || form.codice_sdi || ''
        });
        const sourceLabel = getPivaSourceLabel(res.data.source);
        toast.success(`Trovata: ${res.data.ragione_sociale} (${sourceLabel})`);
      } else if (res.data.valid) {
        setForm({
          ...form,
          partita_iva: res.data.partita_iva_completa || `IT${res.data.partita_iva}`
        });
        toast.info(res.data.message || 'P.IVA valida. Compila manualmente i dati aziendali.', { duration: 5000 });
      } else {
        toast.error(res.data.message || 'P.IVA non valida');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella verifica P.IVA');
    } finally {
      setLookupLoading(false);
    }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/clienti`, form, { withCredentials: true });
      toast.success('Cliente aggiunto');
      setDialogOpen(false);
      resetForm();
      fetchClienti();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo cliente?')) return;
    try {
      await axios.delete(`${API}/clienti/${id}`, { withCredentials: true });
      toast.success('Cliente eliminato');
      fetchClienti();
    } catch { toast.error('Errore'); }
  };

  const openCantiereDialog = (cliente) => {
    setSelectedCliente(cliente);
    setCantiereForm({ name: '', address: cliente.indirizzo ? `${cliente.indirizzo}, ${cliente.citta}` : '', description: '' });
    setCantiereDialogOpen(true);
  };

  const handleCreateCantiere = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/cantieri`, {
        name: cantiereForm.name,
        address: cantiereForm.address,
        description: cantiereForm.description,
        client_name: selectedCliente.ragione_sociale,
        cliente_id: selectedCliente.id
      }, { withCredentials: true });
      toast.success('Cantiere creato e collegato al cliente');
      setCantiereDialogOpen(false);
      fetchClienti();
    } catch { toast.error('Errore'); }
  };

  const resetForm = () => {
    setForm({ ragione_sociale: '', partita_iva: '', codice_fiscale: '', indirizzo: '', citta: '', cap: '', provincia: '', email: '', pec: '', telefono: '', codice_sdi: '', note: '' });
    setPivaInput('');
  };

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return clienti.filter(c =>
      c.ragione_sociale?.toLowerCase().includes(q) ||
      c.partita_iva?.includes(search) ||
      c.citta?.toLowerCase().includes(q)
    );
  }, [clienti, search]);

  // Bulk selection (declared before any early-return)
  const bulkSel = useBulkSelection(filtered, (c) => c.id);

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div></div>;

  return (
    <div className="space-y-6" data-testid="clienti-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900">Clienti</h1>
          <p className="text-slate-500 mt-1">Anagrafica clienti con verifica P.IVA automatica</p>
        </div>
        <Button className="bg-slate-900 text-white hover:bg-slate-800" onClick={() => { resetForm(); setDialogOpen(true); }} data-testid="add-cliente-btn">
          <Plus className="h-4 w-4 mr-2" />Nuovo Cliente
        </Button>
      </div>

      <div className="relative max-w-xs">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <Input placeholder="Cerca per nome, P.IVA o città..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10" data-testid="search-clienti" />
      </div>

      <BulkActionToolbar
        resource="clienti"
        selectedIds={bulkSel.selectedIds}
        onClear={bulkSel.clear}
        onDeleted={fetchClienti}
        itemLabel="cliente"
      />

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filtered.length === 0 ? (
          <Card className="col-span-full border-slate-200"><CardContent className="py-12 text-center"><Building2 className="h-12 w-12 mx-auto text-slate-300 mb-4" /><p className="text-slate-500">Nessun cliente</p></CardContent></Card>
        ) : filtered.map((c) => (
          <Card key={c.id} className="border-slate-200 hover:shadow-sm transition-all relative" data-testid={`cliente-card-${c.id}`} data-selected={bulkSel.isSelected(c.id) ? 'true' : 'false'}>
            <div className="absolute top-2 left-2 z-10">
              <Checkbox
                checked={bulkSel.isSelected(c.id)}
                onCheckedChange={() => bulkSel.toggle(c.id)}
                aria-label={`Seleziona ${c.ragione_sociale}`}
                data-testid={`bulk-select-cliente-${c.id}`}
              />
            </div>
            <CardContent className="p-4 pl-10">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h3 className="font-medium text-slate-900">{c.ragione_sociale}</h3>
                  {c.partita_iva && <p className="text-xs font-mono text-slate-400">P.IVA {c.partita_iva}</p>}
                </div>
                {c.num_cantieri > 0 && (
                  <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 text-xs">
                    <HardHat className="h-3 w-3 mr-1" />{c.num_cantieri} cantieri
                  </Badge>
                )}
              </div>
              {(c.indirizzo || c.citta) && (
                <p className="text-sm text-slate-500 flex items-start gap-1 mb-1">
                  <MapPin className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                  {[c.indirizzo, c.cap, c.citta, c.provincia].filter(Boolean).join(', ')}
                </p>
              )}
              {c.telefono && <p className="text-sm text-slate-500 flex items-center gap-1 mb-1"><Phone className="h-3.5 w-3.5" />{c.telefono}</p>}
              {c.email && <p className="text-sm text-slate-500 flex items-center gap-1 mb-1"><Mail className="h-3.5 w-3.5" />{c.email}</p>}
              {c.pec && <p className="text-xs text-slate-400 mb-1">PEC: {c.pec}</p>}
              {c.codice_sdi && <p className="text-xs text-slate-400 mb-1">SDI: {c.codice_sdi}</p>}
              <div className="flex justify-between items-center pt-3 border-t border-slate-100 mt-3">
                <Button variant="ghost" size="sm" className="text-blue-600 h-8 text-xs" onClick={() => openCantiereDialog(c)} data-testid={`create-cantiere-for-${c.id}`}>
                  <HardHat className="h-3.5 w-3.5 mr-1" />Nuovo Cantiere
                </Button>
                <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500" onClick={() => handleDelete(c.id)}><Trash2 className="h-4 w-4" /></Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Add Cliente Dialog */}
      <Dialog open={dialogOpen} onOpenChange={(o) => { setDialogOpen(o); if (!o) resetForm(); }}>
        <DialogContent className="sm:max-w-[550px] max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Nuovo Cliente</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-4">
            {/* P.IVA Lookup */}
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
              <p className="text-sm font-medium text-blue-700 mb-2">Ricerca automatica per P.IVA</p>
              <div className="flex gap-2">
                <Input placeholder="Es: 01234567890 o IT01234567890" value={pivaInput} onChange={(e) => setPivaInput(e.target.value)} maxLength={16} className="font-mono" data-testid="piva-lookup-input" />
                <Button onClick={handleLookupPiva} disabled={lookupLoading} className="bg-blue-600 text-white hover:bg-blue-700 flex-shrink-0" data-testid="piva-lookup-btn">
                  {lookupLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4 mr-1" />}
                  {lookupLoading ? '' : 'Cerca'}
                </Button>
              </div>
              <p className="text-xs text-blue-500 mt-1">Inserisci la P.IVA per compilare automaticamente i dati</p>
            </div>

            <form onSubmit={handleAdd} className="space-y-4">
              <div className="space-y-2"><Label>Ragione Sociale *</Label><Input value={form.ragione_sociale} onChange={(e) => setForm({...form, ragione_sociale: e.target.value})} required data-testid="cliente-ragione-sociale" /></div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2"><Label>Partita IVA</Label><Input value={form.partita_iva} onChange={(e) => setForm({...form, partita_iva: e.target.value})} className="font-mono" data-testid="cliente-piva" /></div>
                <div className="space-y-2"><Label>Codice Fiscale</Label><Input value={form.codice_fiscale} onChange={(e) => setForm({...form, codice_fiscale: e.target.value})} className="font-mono" /></div>
              </div>
              <div className="space-y-2"><Label>Indirizzo</Label><Input value={form.indirizzo} onChange={(e) => setForm({...form, indirizzo: e.target.value})} data-testid="cliente-indirizzo" /></div>
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2"><Label>CAP</Label><Input value={form.cap} onChange={(e) => setForm({...form, cap: e.target.value})} maxLength={5} /></div>
                <div className="space-y-2"><Label>Città</Label><Input value={form.citta} onChange={(e) => setForm({...form, citta: e.target.value})} /></div>
                <div className="space-y-2"><Label>Provincia</Label><Input value={form.provincia} onChange={(e) => setForm({...form, provincia: e.target.value})} maxLength={2} className="uppercase" /></div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2"><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} /></div>
                <div className="space-y-2"><Label>Telefono</Label><Input value={form.telefono} onChange={(e) => setForm({...form, telefono: e.target.value})} /></div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2"><Label>PEC</Label><Input value={form.pec} onChange={(e) => setForm({...form, pec: e.target.value})} /></div>
                <div className="space-y-2"><Label>Codice SDI</Label><Input value={form.codice_sdi} onChange={(e) => setForm({...form, codice_sdi: e.target.value})} maxLength={7} /></div>
              </div>
              <div className="space-y-2"><Label>Note</Label><Textarea value={form.note} onChange={(e) => setForm({...form, note: e.target.value})} /></div>
              <div className="flex justify-end gap-2 pt-2">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Annulla</Button>
                <Button type="submit" className="bg-slate-900 text-white hover:bg-slate-800" data-testid="save-cliente-btn">Salva Cliente</Button>
              </div>
            </form>
          </div>
        </DialogContent>
      </Dialog>

      {/* Create Cantiere from Cliente */}
      <Dialog open={cantiereDialogOpen} onOpenChange={setCantiereDialogOpen}>
        <DialogContent className="sm:max-w-[450px]">
          <DialogHeader><DialogTitle>Nuovo Cantiere per {selectedCliente?.ragione_sociale}</DialogTitle></DialogHeader>
          <form onSubmit={handleCreateCantiere} className="space-y-4 mt-4">
            <div className="p-3 bg-slate-50 rounded-lg border"><p className="text-xs text-slate-500">Cliente</p><p className="font-medium">{selectedCliente?.ragione_sociale}</p>{selectedCliente?.partita_iva && <p className="text-xs text-slate-400">P.IVA {selectedCliente.partita_iva}</p>}</div>
            <div className="space-y-2"><Label>Nome Cantiere *</Label><Input value={cantiereForm.name} onChange={(e) => setCantiereForm({...cantiereForm, name: e.target.value})} required placeholder="Es: Appartamento Via Roma 15" data-testid="cantiere-name-input" /></div>
            <div className="space-y-2"><Label>Indirizzo Cantiere</Label><Input value={cantiereForm.address} onChange={(e) => setCantiereForm({...cantiereForm, address: e.target.value})} placeholder="Indirizzo del cantiere" data-testid="cantiere-address-input" /></div>
            <div className="space-y-2"><Label>Descrizione</Label><Textarea value={cantiereForm.description} onChange={(e) => setCantiereForm({...cantiereForm, description: e.target.value})} placeholder="Descrizione lavori" /></div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setCantiereDialogOpen(false)}>Annulla</Button>
              <Button type="submit" className="bg-slate-900 text-white hover:bg-slate-800" data-testid="save-cantiere-from-cliente">Crea Cantiere</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
